import { Injectable, OnDestroy } from '@angular/core';
import {
    AccessLevel,
    ApplicationDataType,
    ComboControl,
    ContainerControl,
    ControlType,
    ControlValueOption,
    DataContainer,
    GroupControl,
    IApplicationDataObject,
    IControlWithDefaultValue,
    IControlWithOptions,
    LikertControl,
    MapType,
    Qualifier,
    RepeaterControl,
    RepeaterDisplayStyle,
    dataContainerHasKey,
    getQualifier,
    iApplyFormControl,
    isApplicationDataObject,
    isControlPersistable,
    isControlWithDefaultValue,
    isControlWithObjectData,
    isControlWithOptions,
    isDataObjectWithKey,
    isQualifier
} from 'app/shared/model';
import { deepClone, has, hasValue, isObject } from 'app/shared';
import { DateTime } from 'luxon';
import { ControlHelperService } from './control-helper.service';
import _ from 'lodash';
import { CommunicatorService } from 'app/shared/services/communicator.service';
import { InitialisationService } from 'app/shared/initialisation.service';
import { DataSharingService } from 'app/shared/services/data-sharing.service';
import { CommunicatorCommandsEnum, CommunicatorModel, CommunicatorSetModel } from 'app/shared/services/model/communicatorModel';

@Injectable()
export class ApplicationHelperService
{
    constructor(
        private readonly _controlHelperService: ControlHelperService,
        private readonly _communicatorService: CommunicatorService,
        private readonly _initialisationService: InitialisationService,
        private readonly _dataSharingService: DataSharingService
    ) { }

    flattenControlList(controlList: iApplyFormControl[]): iApplyFormControl[] {
        return this.recursiveFindAll(controlList);
    }

    private recursiveFindAll(controlList: iApplyFormControl[], fn?: (a: iApplyFormControl) => boolean): iApplyFormControl[] {
        let result: Array<iApplyFormControl> = [];

        if (controlList && controlList.length > 0) {
            fn = fn || function () { return true; };
            this._recursiveFindAll(controlList, fn, result);
        }

        return result;
    }

    private _recursiveFindAll(controlList: Array<iApplyFormControl>, fn: (a: iApplyFormControl) => boolean, result: Array<iApplyFormControl>) {
        let controlIterator: iApplyFormControl;
        // for (let i = 0; i < controlList.length; i++) {
        //     controlIterator = controlList[i];

        for (controlIterator of controlList) {
            if (fn(controlIterator)) {
                result.push(controlIterator);
            }

            if (controlIterator && controlIterator.hasOwnProperty('controls') && (<ContainerControl>controlIterator).controls) {
                this._recursiveFindAll((<ContainerControl>controlIterator).controls, fn, result);
            }
        }
    }

    setFieldWithMetadataValue(container, control: iApplyFormControl, value) {
        if (value === undefined || value === null || value === '') {
            container[control.name] = new CommunicatorSetModel(deepClone(control.valueTemplate), 0);
            return;
        }

        // changed it to lodash as structuredClone may cause an issue:
        // Failed to execute 'structuredClone' on 'Window': [object Object] could not be cloned
        let dataObject = _.cloneDeep(container[control.name]);
        if (!isObject(dataObject) || dataObject._type !== control.valueTemplate._type) {
            dataObject = deepClone(control.valueTemplate);
        }

        if (isObject(value) && value._type === control.valueTemplate._type) {
            dataObject = value;
        } else {
            if (typeof value === 'string' && dataObject._type === ApplicationDataType.ValueArray) {
                value = _.map(value.split(','), function (v) { return v.trim(); });
            }

            if (Array.isArray(value)) {
                let options = _.filter((<IControlWithOptions>control).options, function (o) { return value.includes((<ControlValueOption>o).value); });
                dataObject.values = _.map(options, function (o) { return { value: (<ControlValueOption>o).value, description: (<ControlValueOption>o).description }; });
            } else if (typeof value === 'string') {
                let option = _.find((<IControlWithOptions>control).options, function (o) { return value === o.value; });
                if (option) {
                    dataObject = {
                        _type: ApplicationDataType.ValueObject,
                        value: option.value,
                        description: option.description
                    };
                }
            }
        }

        container[control.name] = new CommunicatorSetModel(dataObject, 0);
    }

    setFieldValue(container: DataContainer, key: string, value: unknown, isCalculationUpdate?: boolean) {
        const self = this;
        const qualifier = new Qualifier(key);
        const fieldname = qualifier.pop()?.name ?? key;
        let targetContainer = container;

        const control = this._controlHelperService.findByName(this._dataSharingService.controls, fieldname);

        if (typeof value === 'boolean') {
            value = value ? 'true' : 'false';
        }

        // while (qualifier.length) {
        //     let path = qualifier.pop();
        //     if (Object.hasOwn(targetContainer, path.name)) {
        //         let rows: any[] = targetContainer[path.name];
        //         if (!hasValue(path.index) || rows.length <= path.index) {
        //             rows.push({}); // unshift({});
        //             targetContainer = rows[rows.length - 1];
        //         } else {
        //             targetContainer = rows[path.index];
        //         }
        //     }
        // }

        const getItemCount = function (ctrl: iApplyFormControl, newValue): number {
            if (!Array.isArray(newValue)) {
                return 0;
            }

            let itemCount = newValue.length,
                count = itemCount,
                minCount = -1,
                maxCount = -1;

            if (ctrl && ctrl.type === ControlType.Repeater) {
                let qualifier = getQualifier(container);
                minCount = self.getMinRowCount(<RepeaterControl>ctrl, self._communicatorService.getApplicationData(), qualifier);
                maxCount = self.getMaxRowCount(<RepeaterControl>ctrl, self._communicatorService.getApplicationData(), qualifier);

                if (minCount <= 0 && maxCount <= 0) {
                    return itemCount;
                }

                if (minCount >= 0 && maxCount >= 0 && minCount > maxCount) {
                    minCount = maxCount;
                }

                if (minCount >= 0 && minCount > itemCount) {
                    count = minCount;
                }

                if (maxCount >= 0 && maxCount < itemCount) {
                    count = maxCount;
                }
            }

            return count;
        };

        const getMaxCount = function (ctrl: iApplyFormControl): number {
            let maxCount = -1;

            if (ctrl && ctrl.type === ControlType.Repeater) {
                maxCount = self.getMaxRowCount(<RepeaterControl>ctrl, self._communicatorService.getApplicationData(), getQualifier(container));
            }

            return maxCount;
        };

        // eslint-disable-next-line complexity
        const resetFieldValue = function (currentContainer: object, controlName: string) {
            if (!currentContainer.hasOwnProperty(controlName)) {
                // Property doesn't exist... nothing to initialize.
                return;
            }

            if (Array.isArray(currentContainer[controlName]) || (control && control.type === ControlType.Repeater)) {
                currentContainer[controlName] = new CommunicatorSetModel([], 0, isCalculationUpdate);
                return;
            }

            if (isObject(currentContainer[controlName])) {
                if (currentContainer[controlName].hasOwnProperty('_type')) {
                    if (currentContainer[controlName]._type === ApplicationDataType.ValueArray) {
                        currentContainer[controlName].values = new CommunicatorSetModel([], 0, isCalculationUpdate);
                    } else if (currentContainer[controlName]._type === ApplicationDataType.ValueObject) {
                        currentContainer[controlName].value = new CommunicatorSetModel('', 0, isCalculationUpdate);
                        currentContainer[controlName].description = new CommunicatorSetModel('', 0, isCalculationUpdate);
                    }
                } else {
                    for (let subkey in currentContainer[controlName]) {
                        if (currentContainer[controlName].hasOwnProperty(subkey)) {
                            self.setFieldValue(currentContainer[controlName], subkey, null, isCalculationUpdate);
                        }
                    }
                }

                return;
            }

            if (control !== null && isControlWithObjectData(control)) {
                currentContainer[controlName] = new CommunicatorSetModel(deepClone(control.valueTemplate), 0, isCalculationUpdate);
            } else {
                currentContainer[controlName] = new CommunicatorSetModel('', 0, isCalculationUpdate);
            }
        };

        const populateRepeater = function (objContainer, controlName: string, newValue) {
            let itemCount = newValue.length,
                count = getItemCount(control, newValue),
                maxCount = Math.max(itemCount, count);

            if (!objContainer.hasOwnProperty(controlName) || !Array.isArray(objContainer[controlName])) {
                Object.assign(objContainer, { [controlName]: new CommunicatorSetModel([], 0, isCalculationUpdate) });
            }

            for (let index = 0; index < maxCount; index++) {
                let val = index < newValue.length ? newValue[index] : {};
                self.setFieldValue(objContainer[controlName], index.toString(), val, isCalculationUpdate);
            }
        };

        const setRepeaterFieldValue = function (objContainer, qualifier: Qualifier, controlName: string, rowData) {
            let pathName = qualifier.paths.length > 0 ? qualifier.paths[0].name : null;
            if (!pathName) {
                return;
            }

            populateRepeater(objContainer, pathName, rowData);

            // // patch the data from the value into the corresponding repeater row container...
            // rowData.map((data, index) => {
            //     if (Object.hasOwn(data, controlName)) {
            //         const value = data[controlName];
            //         const row = rows.length > index
            //             ? rows[index]
            //             : rows[rows.push({ $$qualifier: qualifier.createChild(controlName, index) }) - 1];

            //         if (control && isControlWithObjectData(control)) {
            //             self.setFieldWithMetadataValue(row, control, value);
            //             return;
            //         }
            //         self.setFieldValue(row, controlName, value, isCalculationUpdate);
            //     }
            // });
        };

        const setOptionControlValue = function (container: DataContainer, control: iApplyFormControl, values: any[]) {
            if (isControlWithOptions(control) && control.options.length > 0) {
                // check control options for matches in existing option list
                let _val = deepClone(control.valueTemplate);
                let category: string | undefined = undefined
                if (control.optionFilterControl) {
                    category = self.getFieldValue(container, getQualifier(container), control.optionFilterControl);
                    if (isApplicationDataObject(category)) {
                        category = category.value;
                    }
                }

                for (let value of values) {
                    // TODO: Filter by parent category if one is defined?
                    const option = control.options.find(o => o.value == value && (!category || category == o.category));
                    if (option) {
                        _val['values'].push({ value: option.value, description: option.description });
                    }
                }

                container[control.name] = _val;
            }
        };

        const setObjectValue = function (objContainer: DataContainer, controlName: string, newValue) {
            if (Array.isArray(objContainer[controlName])) {
                let maxCount = getMaxCount(control);
                if (maxCount < 0 || objContainer[controlName].length < maxCount) {
                    self.setFieldValue(objContainer[controlName], objContainer[controlName].length.toString(), newValue, isCalculationUpdate);
                }
                return;
            }

            if (newValue.hasOwnProperty('_type') && (newValue._type === ApplicationDataType.ValueArray || newValue._type === ApplicationDataType.ValueObject)) {
                Object.assign(objContainer, { [controlName]: new CommunicatorSetModel(newValue, 0, isCalculationUpdate) });
                return;
            }

            const qualifier = getQualifier(objContainer);
            let useBaseContainer = (qualifier.paths[0]?.name === controlName);

            for (let subkey in newValue) {
                if (newValue.hasOwnProperty(subkey)) {
                    if (useBaseContainer) {
                        // We're already in the correct container, so we're just populating a single row.
                        self.setFieldValue(objContainer, subkey, newValue[subkey], isCalculationUpdate);
                    } else {
                        if (!objContainer[controlName]) {
                            objContainer[controlName] = new CommunicatorSetModel({}, 0, isCalculationUpdate);
                        }

                        self.setFieldValue(objContainer[controlName], subkey, newValue[subkey], isCalculationUpdate);
                    }
                }
            }
        };

        if (value === undefined || value === null || value === '') {
            resetFieldValue(targetContainer, fieldname);
            return;
        }

        if (Array.isArray(value)) {
            //  && (value.length > 0)...
            if (control.type == ControlType.Repeater) {
                populateRepeater(targetContainer, fieldname, value);
                return;
            }

            if (isControlWithOptions(control)) {
                setOptionControlValue(targetContainer, control, value);
                return;
            }

            if (qualifier.length > 0) {
                setRepeaterFieldValue(targetContainer, qualifier, fieldname, value);
                return;
            }
        }

        if (isObject(value)) {
            setObjectValue(targetContainer, fieldname, value);
            return;
        }

        if (control && isControlWithObjectData(control)) {
            this.setFieldWithMetadataValue(targetContainer, control, value);
            return;
        }

        Object.assign(targetContainer, { [fieldname]: new CommunicatorSetModel(value, 0, isCalculationUpdate) });
    };

    setFieldDefaultValue(container: DataContainer, control: iApplyFormControl) {
        if (control.access !== AccessLevel.Write) {
            return;
        }

        if (!this._initialisationService.isControlValueEmpty(container, control)) {
            return;
        }

        if (control.type === ControlType.Repeater) {
            container[control.name] = [];

            if ((<RepeaterControl>control).displayStyle !== RepeaterDisplayStyle.Page) {
                const q = getQualifier(container);
                let controlMinimumCount = this.getMinRowCount(<RepeaterControl>control, this._communicatorService.applicationData, q);
                if (controlMinimumCount !== -1) {
                    for (let i = 0; i < controlMinimumCount; i++) {
                        let newRow = this._initialisationService.initRepeaterChild(control.name, (<RepeaterControl>control).controls, container[control.name], this.getMinRowCount);
                        const parentQualifier = getQualifier(container[control.name]);
                        this.setQualifier(newRow, parentQualifier, control.name, i);
                        // FullPageService.addRepeaterRowSections($rootScope.sections, control, container[control.name][i]); //ToDo
                    }
                    //$rootScope.$emit('fullpageSectionsUpdated', control); //ToDo
                }
                //$rootScope.$emit('RepeaterInitialized-' + control.name); //ToDo
            }
        } else if (control.type === ControlType.Likert) {
            container[control.name] = {};
            for (let qIndex = 0; qIndex < (<LikertControl>control).questions.length; qIndex++) {
                let q = (<LikertControl>control).questions[qIndex];
                container[control.name][q.name] = '';
            }

        } else if (control.type === ControlType.Group) {
            this._initialisationService.initContainer((<GroupControl>control).controls, container, this.getMinRowCount);

        } else if (isControlWithObjectData(control)) {
            this.setFieldWithMetadataValue(container, control, (<IControlWithDefaultValue><unknown>control).defaultValue);

        } else {
            let defaultValue = (<IControlWithDefaultValue><unknown>control).defaultValue;

            if (control.type === ControlType.Datepicker && defaultValue) {
                defaultValue = DateTime.now().getRelativeDate(defaultValue)?.toISODate() || defaultValue;
            }

            let arrDefVal: Array<string> = null;
            if (control.type === ControlType.Checkboxgroup) {
                arrDefVal = defaultValue ? [defaultValue] : [];
            }

            this.setFieldValue(container, control.name, arrDefVal || defaultValue);
        }
    };

    /** sets a new container value based on the control data template. */
    setControlValue(container: DataContainer, control: iApplyFormControl, value?: any) {
        // return true;
    }

    /** Sets the default control value. Called when a control becomes visible. */
    initializeControlValue(control: iApplyFormControl, container: DataContainer, isVisible: boolean) {
        if (control.access !== AccessLevel.Write) {
            // Can't change values of controls without write permission...
            return;
        }

        const currentValue = container[control.name];

        if (control.type === ControlType.Hidden) {
            // Hidden controls are a special case...
            // if (currentValue)
        }

        // console.debug('ApplicationHelperService.initializeControlValue()', control, container);
        const persistWhenHidden = isControlPersistable(control) && control.persistValue;


        // update value of control in container based on visibility...
        if (isVisible) {
            // Visible. if the control has a default value, and no existing value
            if (hasValue(currentValue) && persistWhenHidden) {
                // nothing to do... could have been populated as blank explicitly.
                return;
            }

            if (isControlWithDefaultValue(control) && hasValue(control.defaultValue)) {
                // container
            }


        } else {
            // Hidden. Clear the value for this control, unless it has persistWhenHidden flag.
            if (persistWhenHidden) {
                // don't clear value!
                return;
            }

            this.setControlValue(container, control);
        }
    }

    findFieldValue(qualifier: Qualifier | string, fieldName: string, applicationData: DataContainer, controlList?: iApplyFormControl[]): any {
        let currContainer = applicationData,
            currQualifier = isQualifier(qualifier) ? qualifier.clone() : new Qualifier(<string>qualifier),
            lastContainer: DataContainer = null;

        // if the qualifier of the control value is in a repeater, narrow the container down to those values only
        while (currQualifier.length > 0) {
            if (dataContainerHasKey(currContainer, fieldName)) {
                lastContainer = currContainer;
            }

            let q = currQualifier.shift();
            if (!dataContainerHasKey(currContainer, q.name)) {
                break;
            }

            if (Array.isArray(currContainer)) {
                // Map all matching child repeater rows... if they don't exist, don't include them.
                currContainer = currContainer
                    .map(row => q.index != null ? row[q.name][q.index] : row[q.name])
                    .flat().filter(row => row != undefined);
            } else {
                currContainer = q.index !== null
                    ? currContainer[q.name][q.index]
                    : currContainer[q.name];
            }
        }

        if (lastContainer && !dataContainerHasKey(currContainer, fieldName)) {
            currContainer = lastContainer;
        }

        // does control exist in current data container
        if (currContainer && isDataObjectWithKey(currContainer, fieldName)) {
            return this.getCleanValue(currContainer[fieldName]);
        }

        // If the control is in a repeater, get all matching values in child rows.
        if (currContainer && Array.isArray(currContainer) && currContainer.find(row => Object.hasOwn(row, fieldName))) {
            let results = [];
            for (let row of currContainer) {
                if (Object.hasOwn(row, fieldName)) {
                    results.push(this.getCleanValue(row[fieldName]));
                }
            }
            return results;
        }

        // if not, get value from current row of "Master Repeater" if linked.
        let list = controlList || this._dataSharingService.controls,
            newQualifier = this.findFieldContainerPath(qualifier, fieldName, list, false);

        if (newQualifier.length) {
            if (newQualifier.toString() !== qualifier.toString()) {
                return this.findFieldValue(newQualifier, fieldName, applicationData);
            }
        }

        // Otherwise, get value from main application data container.
        if (currContainer && Array.isArray(currContainer) && currContainer.find(row => Object.hasOwn(row, fieldName)) != null) {
            let results = [];
            for (let row of currContainer) {
                if (Object.hasOwn(row, fieldName)) {
                    results.push(this.getCleanValue(row[fieldName]));
                }
            }

            return results;
        }

        if (!!list && list.length) {
            return this._findControlValue(qualifier, fieldName, list, applicationData);
        }

        return null;
    }

    private _findControlValue(qualifierInput: Qualifier | string, fieldName: string, controlList: iApplyFormControl[], container: DataContainer): any {
        let control = this._controlHelperService.findByName(controlList, fieldName);
        if (!control) {
            return null;
        }

        let qualifier: Qualifier = (qualifierInput instanceof Qualifier) ? qualifierInput : new Qualifier(qualifierInput);
        let newQual = this.getControlPathWithUpdatedIndex(qualifier, fieldName, controlList);

        if (qualifier.toString() !== newQual.toString()) {
            return this.findFieldValue(newQual, fieldName, container, controlList);
        }

        return null;
    }

    getControlPathWithUpdatedIndex(qualifierInput: Qualifier, fieldName: string, controlList: iApplyFormControl[]): Qualifier {
        let control = this._controlHelperService.findByName(controlList, fieldName);
        if (!control) {
            return new Qualifier();
        }

        let qualifier: Qualifier = qualifierInput ?? new Qualifier(),
            pathParts = this._controlHelperService.getControlPath(control, controlList),
            newQual = new Qualifier(),
            i = 0;

        for (; i < pathParts.length && i < qualifier.length; i++) {
            newQual.addPath(pathParts[i], qualifier.paths[i].index);
        }

        for (; i < pathParts.length; i++) {
            newQual.addPath(pathParts[i], null);
        }

        return newQual;
    }

    getCleanValue(valueObj: any): string | string[] | IApplicationDataObject {
        if (isApplicationDataObject(valueObj)) {
            if (valueObj._type === ApplicationDataType.ValueArray) {
                return valueObj.values.map(obj => obj.value) ?? [];
            } else {
                return valueObj.value;
            }
        }

        return valueObj;
    }

    findFieldContainer(qualifier: Qualifier | string, fieldName: string, container: DataContainer, controls: iApplyFormControl[]): Qualifier {
        let dataContainer = container;
        let q = isQualifier(qualifier) ? qualifier.clone() : new Qualifier(<string>qualifier);

        controls = controls || this._dataSharingService.controls;
        let containerPath = this.findFieldContainerPath(q, fieldName, controls);

        if (containerPath.length > 0) {
            return containerPath;
        }
        // TODO: When referring to a .Total field of a repeater this is calling
        // calcs every time repeater changes (row added / removed / another field updated)
        // Can we be smarter?
        while (dataContainer && !has(dataContainer, fieldName) && q.length > 0) {
            let currentPath = q.shift();
            dataContainer = dataContainer[currentPath.name][currentPath.index];
            containerPath.push(currentPath);
        }

        if (dataContainer && has(dataContainer, fieldName)) {
            return containerPath;
        }

        return null;
    };

    findFieldContainerPath(qualifier: Qualifier | string, fieldName: string, controlList: iApplyFormControl[], useSafeFieldNames: boolean = true): Qualifier {
        let result = new Qualifier();

        if (!controlList || controlList.length === 0) {
            return result;
        }

        let q = isQualifier(qualifier) ? qualifier.clone() : new Qualifier(qualifier);
        let currentPath = q.pop();
        if (currentPath == undefined) {
            return result;
        }

        let allRepeaters = this._controlHelperService.findByType<RepeaterControl>(controlList, ControlType.Repeater);
        for (let repeater of allRepeaters) {
            let masterRepeater = repeater.masterRepeater;
            while (masterRepeater) {
                if (!allRepeaters.find(item => item.id == masterRepeater.id)) {
                    allRepeaters.push(masterRepeater);
                }

                masterRepeater = masterRepeater.masterRepeater;
            }
        }

        let fullControlList = _.uniq([...controlList, ...allRepeaters]);
        let control = this._controlHelperService.findByName(fullControlList, fieldName);

        while (currentPath && control?.parentControlId) {
            let parent = this._controlHelperService.findById(fullControlList, control.parentControlId);

            if (parent && parent.type === ControlType.Repeater) {
                if (currentPath.name === parent.name) {
                    //path.unshift(useSafeFieldNames ? selector.replace(selectorName, "['" + selectorName + "']") : selector);
                    result.unshift(currentPath); // add path as a parent
                    if (q.length === 0) {
                        break;
                    }

                    currentPath = q.pop();
                } else {
                    // try to find control inside all repeaters (including master repeaters)
                    let repeater = allRepeaters.find(r => r.name === currentPath.name);
                    while (<RepeaterControl>repeater) {
                        let masterControl = (<RepeaterControl>repeater).masterControl;
                        if (masterControl === parent.name) {
                            // path.unshift(selector.replace(selectorName, useSafeFieldNames ? "['" + parent.Name + "']" : parent.Name));
                            currentPath.name = parent.name;
                            result.unshift(currentPath); // add path as a parent

                            if (q.length === 0) {
                                break;
                            }

                            currentPath = q.pop();
                            break;
                        }

                        repeater = allRepeaters.find(r => r.name === masterControl);
                    }

                    if (q.length === 0) {
                        break;
                    }
                }
            }

            control = parent;
        }

        return result;
    }

    updateContainerQualifiers(allControls: iApplyFormControl[], container: DataContainer, qualifier?: Qualifier) {
        let currentQualifier: Qualifier = qualifier ?? getQualifier(container, false);
        container.$$qualifier = currentQualifier;

        // let repeaters = this.allControls.filter(control => control.type == ControlType.Repeater);
        let allRepeaters = this._controlHelperService.findByType<RepeaterControl>(allControls, ControlType.Repeater);

        for (let prop of Object.keys(container)) {
            if (prop.startsWith('$')) {
                continue;
            }

            let repeater = allRepeaters.find(control => control.name === prop);
            if (repeater && Object.hasOwn(container, repeater.name)) {
                let repeaterContainer = container[repeater.name];
                if (Array.isArray(repeaterContainer)) {
                    for (let i = 0; i < repeaterContainer.length; i++) {
                        this.updateContainerQualifiers(allControls, repeaterContainer[i], currentQualifier.clone().addPath(repeater.name, i));
                    }
                }
            }
        }
    }

    /**
     * Finds a "Container" based on it's path.
     */
    getContainer(qualifier: Qualifier, fieldname: string, container: DataContainer): DataContainer | DataContainer[]
    // | string | ApplicationDataObject | ApplicationDataArray
    {
        let q = qualifier.clone();

        while (container) {
            if (container.hasOwnProperty(fieldname)) {
                return container[fieldname];
            }

            if (q.length === 0) {
                break;
            }

            let currentPath = q.shift();
            if (Object.hasOwn(container, currentPath.name)) {
                container = container[currentPath.name];

                if (hasValue(currentPath.index) && Object.hasOwn(container, currentPath.index)) {
                    container = container[currentPath.index];
                }
            } else {
                return null;
            }
        }

        return null;
    }

    getFieldValue(dataContainer: DataContainer, qualifier: Qualifier, fieldname: string) {
        // let containerQualifier = dataContainer['$$qualifier'];
        let container = dataContainer || {}; // Form Service "applicationData"?
        while (container) {
            if (fieldname && container.hasOwnProperty(fieldname)) {
                return container[fieldname];
            }

            if (!qualifier || qualifier.length === 0) {
                break;
            }

            if (container.hasOwnProperty(qualifier.currentPath.name)
                && qualifier.currentPath.index !== undefined
                && Array.isArray(container)
                && container.length > qualifier.currentPath.index) {
                container = container[qualifier.currentPath.name][qualifier.currentPath.index];
            } else {
                return null;
            }
        }

        return null;
    }

    initContainerValue(container: DataContainer, control: iApplyFormControl) {
        // Only populate if there is no existing pre-populated value.
        if (!this.isControlValueEmpty(container, control)) {
            return;
        }

        const defaultValue = isControlWithDefaultValue(control) ? control.defaultValue : '';
        const noValueTypes = [ControlType.Heading, ControlType.Label, ControlType.Html, ControlType.Group];

        if (!noValueTypes.includes(control.type) && !Object.hasOwn(container, control.name)) {

            let controlValueArray = [];
            let controlValue: any = defaultValue;

            if (control.type === ControlType.Checkboxgroup && defaultValue) {
                controlValueArray.push(defaultValue);
            }

            if (control.type === ControlType.Datepicker && defaultValue) {
                let date = DateTime.now().getRelativeDate(defaultValue);
                controlValue = date.toISODate();
                // controlValue = date.toFormattedString(appConfig.datepicker.altFormatInternal);
            }

            let arrayControlTypes = [ControlType.Checkboxgroup, ControlType.Filebrowser, ControlType.Photobooth, ControlType.Repeater];
            let value = arrayControlTypes.includes(control.type) || (control.type === ControlType.Combo && (<ComboControl>control).allowMultipleSelect)
                ? controlValueArray
                : controlValue;

            if (!hasValue(value) && isControlWithObjectData(control)) {
                value = deepClone(control.valueTemplate); //.toObject();
            }

            container[control.name] = new CommunicatorSetModel(value, 0);
        }
    }

    isControlValueEmpty(container: DataContainer, control: iApplyFormControl) {
        if (!container || !Object.hasOwn(container, control.name)) {
            return true;
        }

        let value = container[control.name];

        if (Array.isArray(value) && value.length === 0) {
            return true;
        }

        if (isObject(value) && value.hasOwnProperty('_type')) {
            if (value._type === ApplicationDataType.ValueObject) {
                return value.value === '';
            }

            if (value._type === ApplicationDataType.ValueArray) {
                return Array(value.values).length === 0;
            }
        }

        return (value === undefined || value === null || value === '');
    }

    setQualifier(
        container: DataContainer,
        qualifier: Qualifier,
        controlName: string,
        index: number,
    ): DataContainer {
        container.$$qualifier = qualifier.createChild(controlName, index);
        return container;
    }

    initControl(control: iApplyFormControl, container: DataContainer): void {
        const controlType = control.type;
        const controlName = control.name;
        const controlControls = (control as any).controls as
            | iApplyFormControl[]
            | undefined;

        this.initContainerValue(container, control);
        let q = getQualifier(container);
        if (control.hasOwnProperty('controls') && controlControls.length > 0) {
            if (controlType === ControlType.Repeater) {
                var controlMinimumCount = this.getMinRowCount(control as RepeaterControl, container, q);
                if (controlMinimumCount === -1) {
                    return;
                }

                for (let j = 0; j < controlMinimumCount; j++) {
                    if (container[controlName].length < controlMinimumCount) {
                        let rowQualifier = getQualifier(container, true);
                        this.initRepeaterChild(
                            controlControls,
                            container[controlName],
                            rowQualifier.createChild(controlName, j));
                    }
                }
            } else {
                controlControls.forEach((control) =>
                    this.initControl(control, container),
                );
            }
        }
    }

    initRepeaterChild(
        controls: iApplyFormControl[],
        container: DataContainer,
        qualifier: Qualifier
    ): DataContainer {
        let template = {
            $$qualifier: qualifier,
        };
        container.push(template);
        template = container[container.length - 1];

        controls.forEach((control) => this.initControl(control, template));

        return container[container.length - 1];
    }

    /**
     * @ngdoc method
     * @name ApplicationEditor.services:ApplicationHelperService#getMinRowCount
     * @methodOf ApplicationEditor.services:ApplicationHelperService
     *
     * @description
     *
     * Get the minimum row count of a repeater control.
     * A '0' or 'less than 0' applies no minimum number of rows.
     * 'greater than 0' applies that number to the minimum number of rows.
     *
     * @param {object} The repeater control.
     * @param {object} The application container.
     */
    getMinRowCount(repeaterControl: RepeaterControl, application: DataContainer, qualifier: Qualifier) {
        if (repeaterControl.masterControl) {
            const container = this.getContainer(qualifier, repeaterControl.masterControl, application);
            return Array.isArray(container) ? container.length : 0;
        }

        let value = !repeaterControl.minimumCountSource
            ? -1
            : repeaterControl.minimumCountSource === MapType.Value
                ? repeaterControl.minimumCount ?? -1
                : this.findFieldValue(qualifier, repeaterControl.minimumCountField, application);

        value = parseInt(value, 10);
        if (isNaN(value) || value < 0) {
            return -1;
        }

        return value;
    }

    /**
     * @ngdoc method
     * @name ApplicationEditor.services:ApplicationHelperService#getMaxRowCount
     * @methodOf ApplicationEditor.services:ApplicationHelperService
     *
     * @description
     *
     * Get the maximum row count of a repeater control.
     * A '0' cannot add any rows.
     * 'greater than 0' applies that number to the maximum number of rows
     *
     * @param {RepeaterControl} repeaterControl The repeater control.
     * @param {DataContainer} application The application container.
     * @param {Qualifier} qualifier The control Qualifier.
     */
    getMaxRowCount(repeaterControl: RepeaterControl, application: DataContainer, qualifier: Qualifier) {
        if (repeaterControl.masterControl) {
            const container = this.getContainer(qualifier, repeaterControl.masterControl, application);
            return Array.isArray(container) ? container.length : 0;
        }

        let value = !repeaterControl.maximumCountSource
            ? -1
            : repeaterControl.maximumCountSource === MapType.Value
                ? repeaterControl.maximumCount ?? -1
                : this.findFieldValue(qualifier, repeaterControl.maximumCountField, application);

        value = parseInt(value, 10);
        if (isNaN(value) || value < 0) {
            return -1;
        }

        return value;
    }
}
